import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Code2, 
  Network, 
  Bot, 
  Terminal, 
  ChevronRight,
  BookOpen,
  GitBranch,
  Package,
} from 'lucide-react';

// Developers Hub - Step 8
const DevelopersHub = () => {
  return (
    <div className="min-h-screen bg-[#F6F7FB]">
      <Helmet>
        <title>Developers | Liquicent</title>
        <meta name="description" content="Build, test, and deploy AI agents on Liquicent. Access our SDK, APIs, and developer resources." />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#0F172A] to-[#1E293B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#2CB1A1]/20 rounded-full mb-6">
              <Terminal className="w-4 h-4 text-[#2CB1A1]" />
              <span className="text-sm font-medium text-[#2CB1A1]">Developer Platform</span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">
              Build Agents That<br />Run Enterprise Workflows
            </h1>
            <p className="text-xl text-[#94A3B8] mb-8 leading-relaxed">
              Create, test, and deploy AI agents using our SDK. Connect to the Unified Transaction Graph 
              and reach thousands of enterprise customers.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/developers/agent-sdk">
                <Button className="bg-white text-[#0F172A] hover:bg-[#F6F7FB]">
                  Get Started with SDK
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/developers/apis">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  API Reference
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <QuickLinkCard 
              icon={Code2}
              title="Agent SDK"
              description="Build agents in Python, TypeScript, or Go"
              link="/developers/agent-sdk"
            />
            <QuickLinkCard 
              icon={Network}
              title="API Reference"
              description="RESTful APIs for workflows and agents"
              link="/developers/apis"
            />
            <QuickLinkCard 
              icon={Bot}
              title="Publish Agent"
              description="Share your agent on the registry"
              link="/developers/publish-agent"
            />
            <QuickLinkCard 
              icon={BookOpen}
              title="Guides"
              description="Tutorials and best practices"
              link="/developers"
            />
          </div>
        </div>
      </section>

      {/* SDK Overview */}
      <section className="py-20 bg-[#F6F7FB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0F172A] mb-6">
                Agent SDK
              </h2>
              <p className="text-lg text-[#475569] mb-8 leading-relaxed">
                Build production-ready agents with our comprehensive SDK. Handle authentication, 
                workflow integration, and transaction logging out of the box.
              </p>
              
              <div className="space-y-4 mb-8">
                <FeatureItem text="Type-safe APIs in Python, TypeScript, and Go" />
                <FeatureItem text="Built-in observability and tracing" />
                <FeatureItem text="Automatic Unified Transaction Graph integration" />
                <FeatureItem text="Local development and testing tools" />
              </div>

              <Link to="/developers/agent-sdk">
                <Button className="bg-[#1A3A8F] hover:bg-[#142D70] text-white">
                  View SDK Documentation
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Code Example */}
            <Card className="p-6 bg-[#0F172A] rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-[#EF4444]" />
                <div className="w-3 h-3 rounded-full bg-[#F59E0B]" />
                <div className="w-3 h-3 rounded-full bg-[#22C55E]" />
              </div>
              <pre className="text-sm text-[#E2E8F0] font-mono overflow-x-auto">
{`from liquicent import Agent, Workflow

class PRVerificationAgent(Agent):
    name = "pr-verification"
    
    async def on_pr_opened(self, pr):
        # Analyze code changes
        analysis = await self.analyze(pr.diff)
        
        # Record decision in UTG
        await self.record_decision(
            type="code_review",
            outcome=analysis.result,
            evidence=analysis.findings
        )
        
        return analysis.recommendation

# Deploy to Liquicent
agent = PRVerificationAgent()
await agent.deploy()`}
              </pre>
            </Card>
          </div>
        </div>
      </section>

      {/* APIs Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-4">API Reference</h2>
            <p className="text-lg text-[#475569] max-w-2xl mx-auto">
              RESTful APIs for workflow management, agent deployment, and analytics
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <APICard 
              title="Workflows API"
              description="Create, manage, and monitor enterprise workflows"
              endpoints={['POST /workflows', 'GET /workflows/{id}', 'PUT /workflows/{id}/trigger']}
            />
            <APICard 
              title="Agents API"
              description="Deploy, configure, and manage AI agents"
              endpoints={['POST /agents/deploy', 'GET /agents/{id}/status', 'POST /agents/{id}/invoke']}
            />
            <APICard 
              title="Analytics API"
              description="Access workflow metrics and agent performance data"
              endpoints={['GET /analytics/workflows', 'GET /analytics/agents', 'GET /analytics/decisions']}
            />
          </div>

          <div className="text-center mt-8">
            <Link to="/developers/apis">
              <Button variant="outline" className="border-[#1A3A8F] text-[#1A3A8F]">
                View Full API Reference
                <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Publish Section */}
      <section className="py-20 bg-gradient-to-br from-[#1A3A8F] to-[#142D70] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Publish to the Agent Registry
              </h2>
              <p className="text-lg text-white/80 mb-8 leading-relaxed">
                Share your agents with enterprise customers and earn revenue from usage. 
                We handle distribution, billing, and support.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-[#2CB1A1]" />
                  <span className="text-white/90">One-click publishing with versioning</span>
                </div>
                <div className="flex items-center gap-3">
                  <GitBranch className="w-5 h-5 text-[#2CB1A1]" />
                  <span className="text-white/90">CI/CD integration for automated releases</span>
                </div>
                <div className="flex items-center gap-3">
                  <Bot className="w-5 h-5 text-[#2CB1A1]" />
                  <span className="text-white/90">Revenue sharing on enterprise deployments</span>
                </div>
              </div>

              <Link to="/developers/publish-agent">
                <Button className="bg-white text-[#1A3A8F] hover:bg-[#F6F7FB]">
                  Start Publishing
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <Card className="p-8 bg-white/10 border border-white/20 rounded-xl">
              <h3 className="font-semibold text-xl mb-6">Publishing Checklist</h3>
              <div className="space-y-4">
                {[
                  'Agent passes all test suites',
                  'Documentation complete',
                  'Privacy and security review',
                  'Pricing tier selected',
                  'Support channels configured',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full border-2 border-white/50 flex items-center justify-center">
                      <span className="text-xs font-medium">{i + 1}</span>
                    </div>
                    <span className="text-white/80">{item}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

const QuickLinkCard = ({ icon: Icon, title, description, link }) => (
  <Link to={link}>
    <Card className="p-6 bg-white border border-[#E5E7EB] rounded-xl hover:shadow-lg hover:border-[#1A3A8F]/20 transition-all h-full group">
      <Icon className="w-8 h-8 text-[#1A3A8F] mb-4 group-hover:scale-110 transition-transform" />
      <h3 className="font-semibold text-[#0F172A] mb-2 group-hover:text-[#1A3A8F] transition-colors">
        {title}
      </h3>
      <p className="text-sm text-[#64748B]">{description}</p>
    </Card>
  </Link>
);

const FeatureItem = ({ text }) => (
  <div className="flex items-center gap-3">
    <div className="w-5 h-5 rounded-full bg-[#2CB1A1]/10 flex items-center justify-center">
      <div className="w-2 h-2 rounded-full bg-[#2CB1A1]" />
    </div>
    <span className="text-[#475569]">{text}</span>
  </div>
);

const APICard = ({ title, description, endpoints }) => (
  <Card className="p-6 bg-[#F6F7FB] border border-[#E5E7EB] rounded-xl">
    <h3 className="font-semibold text-[#0F172A] mb-2">{title}</h3>
    <p className="text-sm text-[#64748B] mb-4">{description}</p>
    <div className="space-y-2">
      {endpoints.map((ep, i) => (
        <code key={i} className="block text-xs font-mono text-[#1A3A8F] bg-white px-2 py-1 rounded">
          {ep}
        </code>
      ))}
    </div>
  </Card>
);

export default DevelopersHub;
